import { Box, TextField, Button, Typography } from '@mui/material';
import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import './signupPageStyles.scss';

const baseUrl = window.location.origin;

const SignupPage = () => {
    useEffect(() => {
        document.title = 'Aggie - Signup';
    }, []);

    const [email, setEmail] = useState('');
    const [firstName, setFirstName] = useState('');
    const [password, setPassword] = useState('');
    const [emailError, setEmailError] = useState('');
    const [firstNameError, setFirstNameError] = useState('');
    const [passwordError, setPasswordError] = useState('');
    const [message, setMessage] = useState('');
    const [showValidationForm, setShowValidationForm] = useState(false);
    const [validationCode, setValidationCode] = useState('');
    const [validationError, setValidationError] = useState('');

    const navigate = useNavigate(); // Use useHistory for navigation

    const validateEmail = (email: string) => {
        const re = /^(([^<>()[\]\\.,;:\s@"]+(\.[^<>()[\]\\.,;:\s@"]+)*)|(".+"))@((\[[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\])|(([a-zA-Z\-0-9]+\.)+[a-zA-Z]{2,}))$/;
        return re.test(String(email).toLowerCase());
    }

    const validateFirstName = (firstName: string) => {
        const re = /^[A-Za-z]+$/;
        return re.test(String(firstName).toLowerCase());
    }

    const validatePassword = (password: string) => {
        const re = /^(?=.*[a-z])(?=.*[A-Z])(?=.*\d)[a-zA-Z\d@$!%*?&]{8,}$/;
        return re.test(password);
    }

    const handleEmailChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        setEmail(e.target.value);
        setEmailError('');
    };

    const handleFirstNameChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        setFirstName(e.target.value);
        setFirstNameError('');
    };

    const handlePasswordChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        setPassword(e.target.value);
        setPasswordError('');
    };

    const handleValidationCodeChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        setValidationCode(e.target.value);
    };

    const handleSubmit = async () => {
        if (!validateEmail(email)) {
            setEmailError('Please enter a valid email address');
            return;
        }
        if (!validateFirstName(firstName)) {
            setFirstNameError('Please enter a valid first name');
            return;
        }
        if (!validatePassword(password)) {
            setPasswordError('Password must contain at least 8 characters, including at least one uppercase letter, one lowercase letter, and one number');
            return;
        }

        try {
            const response = await fetch(`${baseUrl}/signup/email`, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                },
                body: JSON.stringify({ email, firstName, password })
            });

            const data = await response.json();

            if (data.status === 'success') {
                setMessage(data.message);
                setShowValidationForm(true);
            } else if (data.status === 'success' && data.message === 'Email already exists') {
                navigate('/fulfill', { replace: true }); // Redirect to the fulfill page
            } else {
                setMessage(data.error);
            }
        } catch (error) {
            console.error('Error:', error);
            setMessage('An error occurred while trying to sign up.');
        }
    };

    const handleValidationSubmit = async () => {
        try {
            const response = await fetch(`${baseUrl}/validate-email`, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                },
                body: JSON.stringify({ email, validationCode })
            });

            const data = await response.json();

            if (data.is_valid) {
                navigate('/fulfill', { replace: true }); // history.push('/fulfill'); // Redirect to the fulfill page
            } else {
                setValidationError('Invalid validation code. Please try again.');
            }
        } catch (error) {
            console.error('Error:', error);
            setValidationError('An error occurred while validating the code.');
        }
    };

    return (
        <div id="signupPageContainer">
            <div className='pageTitle'>Signup</div>
            <h2 className="text-center mb-10 text-gray-500">Sign up with email</h2>
            <Box
                component="form"
                noValidate
                autoComplete="off"
                className="formContainer"
            >
                {!showValidationForm ? (
                    <>
                        <TextField
                            label="Email"
                            placeholder="Enter Your Email"
                            variant="outlined"
                            onChange={handleEmailChange}
                            className="textField"
                            error={!!emailError}
                            helperText={emailError}
                        />
                        <TextField
                            label="First Name"
                            placeholder="Enter your first name"
                            variant="outlined"
                            onChange={handleFirstNameChange}
                            className="textField"
                            error={!!firstNameError}
                            helperText={firstNameError}
                        />
                        <TextField
                            label="Password"
                            placeholder="Enter your Password"
                            variant="outlined"
                            onChange={handlePasswordChange}
                            className="textField"
                            error={!!passwordError}
                            helperText={passwordError}
                            type="password" // Add this line
                        />
                        <Button onClick={handleSubmit} variant="contained">Submit</Button>
                    </>
                ) : (
                    <>
                        <Typography>{message}</Typography>
                        <TextField
                            label="Validation Code"
                            placeholder="Enter your 6-digit code"
                            variant="outlined"
                            onChange={handleValidationCodeChange}
                            className="textField"
                        />
                        <Button onClick={handleValidationSubmit} variant="contained">Validate</Button>
                        {validationError && <Typography color="error">{validationError}</Typography>}
                    </>
                )}
            </Box>
        </div>
    );
}

export default SignupPage;